import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./ListingCard.module.css";
import getImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import { FaHeart, FaRegHeart, FaBoxOpen } from "react-icons/fa";
import useProductMatchScore from "../../hooks/useProductMatchScore";
import {
  capMatchPercent,
  getMatchBadgeTier,
  formatFitScoreLabel,
  formatFitScoreBadge,
} from "../../utils/matchScoreUtils";
import {
  formatListingPrice,
  formatListingTitle,
  formatViewCount,
} from "../../utils/marketplaceListingFilters";
import {
  formatDistanceAway,
  formatLocationLabel,
  resolveListingDistanceKm,
} from "../../utils/geoUtils";

export const resolveMatchProduct = (listing) => {
  const product = listing?.product;
  if (!product) return null;

  if (typeof product === "object" && (product._id || product.id)) {
    return {
      ...product,
      matchScore: listing.matchScore ?? product.matchScore,
    };
  }

  if (typeof product === "string") {
    return {
      _id: product,
      matchScore: listing.matchScore,
      category: listing.category,
    };
  }

  return null;
};

const MatchScoreBadge = ({ score, compact = false }) => {
  if (score == null) return null;

  const tier = getMatchBadgeTier(score);
  const tierClass =
    tier === "excellent"
      ? styles.matchBadgeExcellent
      : tier === "good"
        ? styles.matchBadgeGood
        : tier === "fair"
          ? styles.matchBadgeFair
          : styles.matchBadgeLow;

  return (
    <div
      className={`${styles.matchBadge} ${tierClass} ${compact ? styles.matchBadgeCompact : ""}`}
      title={formatFitScoreLabel(score)}
    >
      {formatFitScoreBadge(score)}
    </div>
  );
};

const ListingLocation = ({ location, distanceKm }) => {
  const locationLabel = formatLocationLabel(location);
  const distanceLabel = formatDistanceAway(distanceKm);

  if (!locationLabel && !distanceLabel) return null;

  return (
    <div className={styles.locationBlock}>
      {locationLabel && <div className={styles.locationName}>{locationLabel}</div>}
      {distanceLabel && <div className={styles.distanceAway}>{distanceLabel}</div>}
    </div>
  );
};

// Rendered as a sibling of the card's full-cover overlay link (not nested
// inside it) so the seller profile link stays independently focusable and
// clickable — nesting an interactive element inside another interactive
// element is invalid HTML and breaks keyboard/screen-reader navigation.
const SellerBadges = ({ seller }) => (
  <>
    {seller.seller?.isVerified && (
      <span className={styles.sellerVerified} aria-label="Verified seller">
        ✓
      </span>
    )}
    {seller.seller?.badges?.length > 0 && (
      <span className={styles.sellerBadge} aria-label="Seller badge">
        ★
      </span>
    )}
  </>
);

const SellerRow = ({ seller }) => {
  if (!seller) return null;

  return (
    <div className={styles.seller}>
      <img
        src={getImageUrl(seller.avatar, "/fallback.png")}
        alt={seller.firstName}
        className={styles.sellerAvatar}
      />
      {seller._id ? (
        <Link
          to={`/marketplace/seller/${seller._id}`}
          className={styles.sellerLink}
          onClick={(e) => e.stopPropagation()}
        >
          {seller.firstName} {seller.lastName}
          <SellerBadges seller={seller} />
        </Link>
      ) : (
        <span>
          {seller.firstName} {seller.lastName}
          <SellerBadges seller={seller} />
        </span>
      )}
    </div>
  );
};

const ListingCard = ({
  listing,
  onSave,
  viewMode = "grid",
  size = "default",
  userLocation = null,
  showMatchScore = true,
  preferCatalogImage = false,
  // When a parent grid has already resolved match scores for all its cards
  // via a single batched request (see useProductGridMatchScores), pass the
  // resolved value here so this card skips its own per-card network call.
  precomputedMatchScore = null,
  // Some embeds (e.g. the "related listings" strip) don't wire up saving;
  // hide the heart button entirely rather than rendering a dead control.
  hideSaveButton = false,
}) => {
  const isCompact = size === "compact";
  const matchProduct = useMemo(() => resolveMatchProduct(listing), [listing]);
  const enhancedMatchScore = useProductMatchScore(matchProduct, {
    precomputed: precomputedMatchScore,
  });

  const matchScore = useMemo(() => {
    if (enhancedMatchScore != null) return enhancedMatchScore;

    const existingScore =
      listing?.matchScore ??
      matchProduct?.matchScore ??
      matchProduct?.score ??
      matchProduct?.match ??
      null;

    if (existingScore == null) return null;
    return capMatchPercent(existingScore);
  }, [enhancedMatchScore, listing?.matchScore, matchProduct]);

  const distanceKm = useMemo(
    () => resolveListingDistanceKm(listing, userLocation),
    [listing, userLocation]
  );

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave) {
      onSave(listing._id, e);
    }
  };

  const catalogImagePath = resolveProductImagePath(listing.product);
  const listingPhoto = listing.photos?.[0];
  const imagePath =
    preferCatalogImage && catalogImagePath
      ? catalogImagePath
      : listingPhoto || catalogImagePath;
  const displayTitle = formatListingTitle(listing.title);
  const displayPrice = formatListingPrice(listing.price);
  const priceInsight = listing.priceInsight;
  const fairDealLabel =
    priceInsight?.tone === "great" || priceInsight?.label === "Great deal"
      ? priceInsight.label
      : priceInsight?.tone === "fair"
        ? priceInsight.label
        : null;

  return (
    <article
      className={`${styles.listingCard} ${viewMode === "list" ? styles.listView : ""} ${isCompact ? styles.compact : ""}`}
    >
      {/* Full-cover link so the whole card remains clickable while keeping
          the save button and seller link as independently focusable
          siblings instead of nesting interactive elements inside a Link. */}
      <Link
        to={`/marketplace/${listing._id}`}
        className={styles.cardLink}
        aria-label={`View listing: ${displayTitle}`}
      />
      <div className={styles.listingImage}>
        {imagePath ? (
          <img
            src={getImageUrl(imagePath)}
            alt={displayTitle}
            loading="lazy"
          />
        ) : (
          <div className={styles.noImage}>
            <FaBoxOpen />
          </div>
        )}
        {showMatchScore && (
          <MatchScoreBadge score={matchScore} compact={isCompact} />
        )}
        {!hideSaveButton && (
          <button
            type="button"
            className={`${styles.saveButton} ${listing.savedByCurrentUser ? styles.saveButtonSaved : ""}`}
            onClick={handleSaveClick}
            title={listing.savedByCurrentUser ? "Unsave" : "Save listing"}
            aria-label={listing.savedByCurrentUser ? "Unsave listing" : "Save listing"}
          >
            {listing.savedByCurrentUser ? <FaHeart /> : <FaRegHeart />}
          </button>
        )}
        {listing.boostedUntil && new Date(listing.boostedUntil) > new Date() && (
          <span className={`${styles.conditionBadge} ${styles.boostedBadge}`}>
            Boosted
          </span>
        )}
      </div>

      <div className={styles.listingContent}>
        {viewMode === "list" ? (
          <>
            <div className={styles.listViewInfo}>
              <h3 className={styles.listingTitle}>{displayTitle}</h3>
              <ListingLocation location={listing.location} distanceKm={distanceKm} />
              <SellerRow seller={listing.seller} />
            </div>
            <div className={styles.listViewEnd}>
              <div className={styles.listingMeta}>
                <span className={styles.price}>{displayPrice}</span>
                {listing.negotiable && (
                  <span className={styles.negotiable}>Negotiable</span>
                )}
{fairDealLabel && (
                  <span
                    className={[
                      styles.fairDealBadge,
                      priceInsight.tone === "great" ? styles.fairDealGreat : styles.fairDealFair,
                    ].join(" ")}
                  >
                    {fairDealLabel}
                  </span>
                )}
              </div>
              <div className={styles.listingFooter}>
                <span className={styles.views}>
                  {formatViewCount(listing.views || 0)}
                </span>
                <span className={styles.date}>
                  {new Date(listing.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <h3 className={styles.listingTitle}>{displayTitle}</h3>
            <div className={styles.listingMeta}>
              <span className={styles.price}>{displayPrice}</span>
              {listing.negotiable && (
                <span className={styles.negotiable}>Negotiable</span>
              )}
{fairDealLabel && (
                <span
                  className={[
                    styles.fairDealBadge,
                    priceInsight.tone === "great" ? styles.fairDealGreat : styles.fairDealFair,
                  ].join(" ")}
                >
                  {fairDealLabel}
                </span>
              )}
            </div>
            <ListingLocation location={listing.location} distanceKm={distanceKm} />
            <SellerRow seller={listing.seller} />
            <div className={styles.listingFooter}>
              <span className={styles.views}>
                {formatViewCount(listing.views || 0)}
              </span>
              <span className={styles.date}>
                {new Date(listing.createdAt).toLocaleDateString()}
              </span>
            </div>
          </>
        )}
      </div>
    </article>
  );
};

export default ListingCard;
